import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { MdInfoOutline } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AxiosError } from 'axios';
import useSnackbar from '@hooks/useSnackbar';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import Dialog from '@components/2-molecules/Dialog';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import ButtonAtom, {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_OUTLINE,
  BUTTON_VARIANT_TEXT,
} from '@components/1-atoms/Button';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  listLocalRunners,
  deleteLocalRunner,
  downloadRunner,
} from '@api/localRunners';
import type { ApiKeyItem, CreateApiKeyResponse, LocalRunner } from '@api/localRunners';
import { Frame, TableContainer } from '../Settings.styled';
import {
  Wrapper,
  Description,
  DownloadSection,
  DownloadLabel,
  DownloadActions,
  InfoButton,
  InfoDialogBody,
  InfoDialogContent,
  InputRow,
  InputGroup,
  SmallLabel,
  Input,
  InputError,
  WarningText,
  KeyDisplay,
  RunnerTable,
  StatusDot,
  MonoText,
  EmptyState,
} from './LocalRunners.styled';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusColor(status: string): string {
  switch (status) {
    case 'READY':
      return '#4caf50';
    case 'ALLOCATED':
      return '#ff9800';
    default:
      return '#f44336';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'READY':
      return 'Online';
    case 'ALLOCATED':
      return 'Busy';
    case 'OFFLINE':
      return 'Offline';
    default:
      return status;
  }
}

const REMARK_PLUGINS = [remarkGfm];

interface ApiErrorBody {
  readonly error?: string;
}

function serverErrorMessage(err: unknown, expectedStatus: number): string | null {
  const ax = err as AxiosError<ApiErrorBody> | undefined;
  if (ax?.isAxiosError !== true) return null;
  if (ax.response?.status !== expectedStatus) return null;
  const message = ax.response?.data?.error;
  return typeof message === 'string' && message.length > 0 ? message : null;
}

const LOCAL_RUNNER_INFO = `The Aibuild Local Runner lets you execute AI agent commands directly on your own machine instead of a cloud VM, giving you full control over your local CAD installations and files.

**To get started:**

1. Download the runner for your operating system using the button below
2. Generate an API key in **Settings** \u2192 **Local Runners**
3. Run the installer and enter your API key when prompted

The runner will appear as a selectable environment in the chat interface.`;

const mdComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const LocalRunners: React.FC = () => {
  const intl = useIntl();
  const { showSuccess, showError } = useSnackbar();

  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [serverNameError, setServerNameError] = useState('');
  const [generatedKey, setGeneratedKey] = useState<CreateApiKeyResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const isGeneratingRef = useRef(false);

  const [runners, setRunners] = useState<LocalRunner[]>([]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; vmId: string; name: string }>({
    open: false,
    vmId: '',
    name: '',
  });

  const loadApiKeys = useCallback(async () => {
    try {
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch {
      showError(intl.formatMessage({ id: 'runners.error.loadKeys', defaultMessage: 'Failed to load API keys' }));
    }
  }, [showError, intl]);

  const loadRunners = useCallback(async () => {
    try {
      const data = await listLocalRunners();
      setRunners(data);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
    loadRunners();
    const interval = setInterval(loadRunners, 10000);
    return () => clearInterval(interval);
  }, [loadApiKeys, loadRunners]);

  // Derived, not stored: revoking the clashing key must clear this without the user re-editing the field.
  const duplicateNameError = useMemo(() => {
    const trimmed = newKeyName.trim();
    if (!trimmed) return '';
    const isDuplicate = apiKeys.some((k) => k.name.toLowerCase() === trimmed.toLowerCase());
    return isDuplicate
      ? intl.formatMessage({ id: 'runners.apiKeys.nameDuplicate', defaultMessage: 'A key with this name already exists' })
      : '';
  }, [apiKeys, newKeyName, intl]);

  const nameError = serverNameError || duplicateNameError;

  const handleNameChange = useCallback((value: string) => {
    setNewKeyName(value);
    setServerNameError('');
  }, []);

  const handleGenerateKey = useCallback(async () => {
    if (isGeneratingRef.current) return;
    if (!newKeyName.trim() || nameError) return;
    isGeneratingRef.current = true;
    setIsGenerating(true);
    try {
      const result = await createApiKey(newKeyName.trim());
      setGeneratedKey(result);
      setNewKeyName('');
      setServerNameError('');
      await loadApiKeys();
      showSuccess(intl.formatMessage({ id: 'runners.key.generated', defaultMessage: 'API key generated' }));
    } catch (err) {
      const conflictMessage = serverErrorMessage(err, 409);
      if (conflictMessage) {
        setServerNameError(conflictMessage);
        showError(conflictMessage);
        await loadApiKeys();
        return;
      }
      showError(intl.formatMessage({ id: 'runners.error.generateKey', defaultMessage: 'Failed to generate API key' }));
    } finally {
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  }, [newKeyName, nameError, loadApiKeys, showSuccess, showError, intl]);

  const handleRevokeKey = useCallback(
    async (keyId: string) => {
      try {
        await revokeApiKey(keyId);
        setServerNameError('');
        await loadApiKeys();
        showSuccess(intl.formatMessage({ id: 'runners.key.revoked', defaultMessage: 'API key revoked' }));
      } catch {
        showError(intl.formatMessage({ id: 'runners.error.revokeKey', defaultMessage: 'Failed to revoke API key' }));
      }
    },
    [loadApiKeys, showSuccess, showError, intl],
  );

  const handleCopy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showSuccess(intl.formatMessage({ id: 'runners.copied', defaultMessage: 'Copied to clipboard' }));
    },
    [showSuccess, intl],
  );

  const handleDownloadRunner = useCallback(async () => {
    setIsDownloading(true);
    try {
      await downloadRunner();
    } catch {
      showError(intl.formatMessage({ id: 'runners.download.error', defaultMessage: 'Failed to start download' }));
    } finally {
      setIsDownloading(false);
    }
  }, [showError, intl]);

  const handleDeleteRunner = useCallback(async () => {
    if (!deleteConfirm.vmId) return;
    setDeleteConfirm({ open: false, vmId: '', name: '' });
    try {
      await deleteLocalRunner(deleteConfirm.vmId);
      await loadRunners();
      showSuccess(intl.formatMessage(
        { id: 'runners.runner.deleted', defaultMessage: 'Runner "{name}" deleted' },
        { name: deleteConfirm.name },
      ));
    } catch {
      showError(intl.formatMessage({ id: 'runners.error.deleteRunner', defaultMessage: 'Failed to delete runner' }));
    }
  }, [deleteConfirm, loadRunners, showSuccess, showError, intl]);

  return (
    <Wrapper data-testid="local-runners-settings">
      <ConfirmDialog
        show={deleteConfirm.open}
        title={intl.formatMessage({ id: 'runners.delete.title', defaultMessage: 'Delete Runner' })}
        confirmLabel={intl.formatMessage({ id: 'runners.delete.confirm', defaultMessage: 'Delete' })}
        onConfirm={handleDeleteRunner}
        onCancel={() => setDeleteConfirm({ open: false, vmId: '', name: '' })}
      >
        <p>
          {intl.formatMessage(
            {
              id: 'runners.delete.message',
              defaultMessage: 'Delete runner "{name}"? It will be removed from the list. If it\'s still running, it will re-register on next heartbeat.',
            },
            { name: deleteConfirm.name },
          )}
        </p>
      </ConfirmDialog>

      <Description>
        {intl.formatMessage({
          id: 'runners.description',
          defaultMessage: 'Run Aibuild OS agents on your own machine using your local CAD software.',
        })}
      </Description>

      <DownloadSection>
        <DownloadLabel>
          {intl.formatMessage({
            id: 'runners.download.label',
            defaultMessage: 'Download the runner to connect your local CAD software to Aibuild OS.',
          })}
        </DownloadLabel>
        <DownloadActions>
          <ButtonAtom
            variant={BUTTON_VARIANT_PRIMARY}
            onClick={handleDownloadRunner}
            disabled={isDownloading}
            stretch={false}
          >
            {isDownloading
              ? intl.formatMessage({ id: 'runners.download.downloading', defaultMessage: 'Preparing download...' })
              : intl.formatMessage({ id: 'runners.download.button', defaultMessage: 'Download Runner' })}
          </ButtonAtom>
          <InfoButton
            onClick={() => setInfoOpen(true)}
            aria-label={intl.formatMessage({
              id: 'runners.info.ariaLabel',
              defaultMessage: 'About Local Runner',
            })}
          >
            <MdInfoOutline size={20} />
          </InfoButton>
        </DownloadActions>
      </DownloadSection>

      {infoOpen && (
        <Dialog
          show={infoOpen}
          headerTitle={intl.formatMessage({
            id: 'runners.info.title',
            defaultMessage: 'Aibuild Local Runner',
          })}
          onClose={() => setInfoOpen(false)}
          onCloseIconButtonClick={() => setInfoOpen(false)}
          primaryButtonLabel={intl.formatMessage({
            id: 'runners.info.close',
            defaultMessage: 'Close',
          })}
          onPrimaryButtonClick={() => setInfoOpen(false)}
          closeOnEscape
          closeOnClickOutside
        >
          <InfoDialogBody>
            <InfoDialogContent>
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={mdComponents}>
                {LOCAL_RUNNER_INFO}
              </ReactMarkdown>
            </InfoDialogContent>
          </InfoDialogBody>
        </Dialog>
      )}

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'runners.apiKeys.title', defaultMessage: 'API Keys' })}
      />
      <Frame>
        <InputRow>
          <InputGroup>
            <SmallLabel>
              {intl.formatMessage({ id: 'runners.apiKeys.nameLabel', defaultMessage: 'Key Name' })}
            </SmallLabel>
            <Input
              placeholder={intl.formatMessage({ id: 'runners.apiKeys.namePlaceholder', defaultMessage: 'Name your key, e.g. My Workstation' })}
              value={newKeyName}
              $hasError={!!nameError}
              onChange={(e) => handleNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleGenerateKey();
                }
              }}
            />
          </InputGroup>
          <ButtonAtom
            variant={BUTTON_VARIANT_PRIMARY}
            onClick={handleGenerateKey}
            disabled={isGenerating || !newKeyName.trim() || !!nameError}
            stretch={false}
          >
            {isGenerating
              ? intl.formatMessage({ id: 'runners.apiKeys.generating', defaultMessage: 'Generating...' })
              : intl.formatMessage({ id: 'runners.apiKeys.generate', defaultMessage: 'Generate Key' })}
          </ButtonAtom>
        </InputRow>
        {nameError && <InputError>{nameError}</InputError>}

        {generatedKey && (
          <>
            <WarningText>
              {intl.formatMessage({
                id: 'runners.apiKeys.warning',
                defaultMessage: 'Copy this key now — it will not be shown again.',
              })}
            </WarningText>
            <KeyDisplay>
              <span>{generatedKey.key}</span>
              <ButtonAtom variant={BUTTON_VARIANT_TEXT} onClick={() => handleCopy(generatedKey.key)} stretch={false}>
                {intl.formatMessage({ id: 'runners.copy', defaultMessage: 'Copy' })}
              </ButtonAtom>
            </KeyDisplay>
          </>
        )}

        {apiKeys.length > 0 ? (
          <TableContainer>
            <RunnerTable>
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: 'runners.apiKeys.name', defaultMessage: 'Name' })}</th>
                  <th>{intl.formatMessage({ id: 'runners.apiKeys.key', defaultMessage: 'Key' })}</th>
                  <th>{intl.formatMessage({ id: 'runners.apiKeys.created', defaultMessage: 'Created' })}</th>
                  <th>{intl.formatMessage({ id: 'runners.apiKeys.lastUsed', defaultMessage: 'Last Used' })}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td>
                      <MonoText>{key.keyPrefix}...</MonoText>
                    </td>
                    <td>{formatDate(key.createdAt)}</td>
                    <td>{formatDate(key.lastUsedAt)}</td>
                    <td>
                      <ButtonAtom
                        variant={BUTTON_VARIANT_OUTLINE}
                        onClick={() => handleRevokeKey(key.id)}
                        stretch={false}
                      >
                        {intl.formatMessage({ id: 'runners.apiKeys.revoke', defaultMessage: 'Revoke' })}
                      </ButtonAtom>
                    </td>
                  </tr>
                ))}
              </tbody>
            </RunnerTable>
          </TableContainer>
        ) : (
          <EmptyState>
            {intl.formatMessage({
              id: 'runners.apiKeys.empty',
              defaultMessage: 'No API keys yet. Generate one to connect a local runner.',
            })}
          </EmptyState>
        )}
      </Frame>

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'runners.connected.title', defaultMessage: 'Connected Runners' })}
      />
      <Frame>
        {runners.length > 0 ? (
          <TableContainer>
            <RunnerTable>
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: 'runners.connected.status', defaultMessage: 'Status' })}</th>
                  <th>{intl.formatMessage({ id: 'runners.connected.name', defaultMessage: 'Name' })}</th>
                  <th>{intl.formatMessage({ id: 'runners.connected.apps', defaultMessage: 'Apps' })}</th>
                  <th>{intl.formatMessage({ id: 'runners.connected.lastSeen', defaultMessage: 'Last Seen' })}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runners.map((runner) => (
                  <tr key={runner.vmId}>
                    <td>
                      <StatusDot $color={statusColor(runner.status)} />
                      {statusLabel(runner.status)}
                    </td>
                    <td>{runner.name}</td>
                    <td>{runner.installedApps.join(', ') || '—'}</td>
                    <td>{formatDate(runner.lastHeartbeat)}</td>
                    <td>
                      <ButtonAtom
                        variant={BUTTON_VARIANT_OUTLINE}
                        onClick={() => setDeleteConfirm({ open: true, vmId: runner.vmId, name: runner.name })}
                        stretch={false}
                      >
                        {intl.formatMessage({ id: 'runners.connected.delete', defaultMessage: 'Delete' })}
                      </ButtonAtom>
                    </td>
                  </tr>
                ))}
              </tbody>
            </RunnerTable>
          </TableContainer>
        ) : (
          <EmptyState>
            {intl.formatMessage({
              id: 'runners.connected.empty',
              defaultMessage: 'No runners connected. Start a runner on your machine to see it here.',
            })}
          </EmptyState>
        )}
      </Frame>
    </Wrapper>
  );
};

export default LocalRunners;
